import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import NativeSelect from "@mui/material/NativeSelect";
import OutlinedInput from "@mui/material/OutlinedInput";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Search } from "lucide-react";
import { messageFor } from "@/lib/errors";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { NotYetAvailable } from "@/components/NotYetAvailable";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageHeader } from "@/components/PageHeader";
import { useListUrlState } from "@/base/list/useListUrlState";
import {
  useFilterOptions,
  type FilterOptions,
} from "@/base/list/useFilterOptions";
import {
  isPendingListView,
  type AnyListViewDef,
  type BaseListViewDef,
  type ListColumn,
  type ListFilter,
  type ListRequest,
  type ListRowContext,
  type ListSort,
  type ListSortState,
  type ListTotals,
  type ListViewState,
  type Page,
  type PendingListViewDef,
} from "@/base/list/types";

// BaseListView — the list every Broker Portal list page is built on (BRK-S211;
// React base components + typed definitions, NOT class inheritance, like
// BaseForm). A page hands it a definition (columns, search, toggles, sort, a
// loader) and inherits the rest:
//
// - the view in the URL (useListUrlState), so a link, reload or Back shows the
//   same list;
// - server paging from PMS's `total` ("Showing 21–40 of 128");
// - a table at md+ and cards below it, from ONE definition and ONE DOM (never a
//   copy hidden by CSS);
// - the loading, error and empty states, designed once.
//
// The look comes from the account base design's own lists (Policies in force,
// Invoices): a bordered card, small uppercase headers, the first column as the
// row's link. Sort sits on the right of the toolbar, and the pager (the count
// plus Previous/Next) is a dock that stays on the bottom edge while the rows
// scroll (Nav, 2026-09-27): on the viewport's edge below lg, on the sticky site
// footer's top at lg+. Its `data-list-pager` sets --brk-dock-h (index.css), so
// the scroll cue and the focus scroll-padding make room for it. It also makes
// the list fill the content area, so under a short list or at the list's end
// the pager still sits on the bottom edge, not under the last row (BRK-S250).

const SKELETON_ROWS = 5;
/** How long the search box waits for the broker to stop typing (BRK-S315),
 *  as `Typeahead` does. */
const SEARCH_PAUSE_MS = 300;

/** For a line a screen reader needs but the eye doesn't. Pixel strings on
 *  purpose: in `sx`, `width: 1` means 100% and `m: -1` a spacing step. */
const srOnly = {
  position: "absolute",
  width: "1px",
  height: "1px",
  p: 0,
  m: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

const DATE_DIRECTIONS = { desc: "newest first", asc: "oldest first" };

function headerOf<Row>(column: ListColumn<Row>, view: ListViewState): string {
  return typeof column.header === "function"
    ? column.header(view)
    : column.header;
}

/** A list page's one component: a definition PMS serves (`defineListView`) or
 *  one it doesn't yet (`definePendingListView`, BRK-S251). */
export function BaseListView<Row>({
  def,
  embedded = false,
}: {
  def: AnyListViewDef<Row>;
  /** Inside a page that has its own heading (BRK-S253): no header here. */
  embedded?: boolean;
}) {
  return isPendingListView(def) ? (
    <PendingListView def={def} />
  ) : (
    <LiveListView def={def} embedded={embedded} />
  );
}

function LiveListView<Row>({
  def,
  embedded,
}: {
  def: BaseListViewDef<Row>;
  embedded: boolean;
}) {
  const filterOptions = useFilterOptions(def.filters);
  const url = useListUrlState(def, filterOptions);
  const theme = useTheme();
  // A `wide` definition's table needs lg (BRK-S501); below it, cards.
  const wide = useMediaQuery(theme.breakpoints.up(def.wide ? "lg" : "md"), {
    noSsr: true,
  });
  const view: ListViewState = { sort: url.sort };

  const request: ListRequest = {
    search: url.search || undefined,
    toggles: url.toggles,
    filters: url.filters,
    links: url.links,
    sort: url.sort,
    limit: def.pageSize,
    offset: (url.page - 1) * def.pageSize,
  };
  // The load re-runs when the request changes, not whenever the page renders.
  const requestKey = JSON.stringify(request);
  const latest = useRef({ load: def.load, request });
  latest.current = { load: def.load, request };

  const [page, setPage] = useState<Page<Row> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // A row action that changes the list (Mark read, Archive) re-reads the page.
  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const rowContext: ListRowContext = { reload };

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    // A filter value in the URL waits for its options (BRK-S254), so the list
    // waits with it rather than showing more than the URL asks for.
    if (url.waiting) return;
    latest.current
      .load(latest.current.request)
      .then((result) => {
        if (active) setPage(result);
      })
      .catch((err: unknown) => {
        if (active) setError(messageFor(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [requestKey, attempt, url.waiting]);

  // A new page starts at the list's top, not wherever Next was clicked.
  const top = useRef<HTMLElement>(null);
  const shownPage = useRef(url.page);
  useEffect(() => {
    if (shownPage.current !== url.page) {
      shownPage.current = url.page;
      top.current?.scrollIntoView({ block: "start" });
    }
  }, [url.page]);

  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const firstLoad = loading && page === null;
  // Narrowed by the list's own controls. A link param narrows too, but its
  // note already says so, so an empty list there is simply empty.
  const narrowed =
    Object.values(url.toggles).some(Boolean) ||
    Object.values(url.filters).some(Boolean);
  const filtered = Boolean(url.search) || narrowed;
  const from = total === 0 ? 0 : request.offset + 1;
  const to = Math.min(request.offset + items.length, total);
  const lastPage = Math.max(1, Math.ceil(total / def.pageSize));
  // The pager shows with rows to page through; the error, empty and loading
  // states have none.
  const paged = !error && items.length > 0;

  return (
    <Box
      component="section"
      ref={top}
      sx={{
        // A column the page column stretches (index.css), so the pager's auto
        // top margin takes the slack under a short list.
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        // A new page lands where the list starts: the html's scroll-padding
        // clears the top bar, and this is main's padding above the header.
        scrollMarginTop: "1.5rem",
      }}
    >
      {!embedded && (
        <ListHeader
          title={def.title}
          context={def.context}
          action={def.primaryAction}
        />
      )}

      <Toolbar
        def={def}
        url={url}
        filterOptions={filterOptions}
        // Beside the filters while they narrow rows that are showing (BRK-S406);
        // a filtered-empty list offers its own in the empty state instead.
        clearable={narrowed && !error && items.length > 0}
      />

      {/* Pre-mounted, so a screen reader hears every load start. Visible on
          the first load; a refetch shows the progress bar instead. */}
      <Typography
        role="status"
        sx={
          firstLoad
            ? { typography: "body2", color: "text.secondary", mb: 1 }
            : srOnly
        }
      >
        {loading ? `Loading ${def.noun}…` : ""}
      </Typography>

      {/* A backstop for anything the rows' own boundaries can't catch (the
          skeleton, the empty state, a column header that throws, BRK-S259):
          the list says so, with Retry, and the header, the toolbar and the
          rest of the page stay. */}
      <ErrorBoundary
        resetKeys={[requestKey, attempt]}
        fallback={({ reset }) => (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  reset();
                  reload();
                }}
              >
                Retry
              </Button>
            }
          >
            These {def.noun} couldn’t be shown.
          </Alert>
        )}
      >
        {error ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setAttempt((n) => n + 1)}
              >
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : firstLoad ? (
          <SkeletonRows def={def} view={view} wide={wide} />
        ) : items.length === 0 ? (
          <EmptyState
            def={def}
            search={url.search}
            filtered={filtered}
            pastTheEnd={total > 0}
            onClearSearch={() => url.setSearch("")}
            onClearFilters={url.clearFilters}
            onFirstPage={() => url.setPage(1)}
          />
        ) : (
          <Box sx={{ position: "relative", mb: 2 }}>
            {loading && (
              <LinearProgress
                aria-label={`Loading ${def.noun}`}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 1,
                }}
              />
            )}
            {wide ? (
              <RowsTable
                def={def}
                rows={items}
                view={view}
                context={rowContext}
                totals={page?.totals}
              />
            ) : (
              <RowsCards
                def={def}
                rows={items}
                view={view}
                context={rowContext}
                totals={page?.totals}
              />
            )}
          </Box>
        )}
      </ErrorBoundary>

      {paged && (
        <Box
          data-list-pager
          sx={{
            position: "sticky",
            // On the viewport's edge, then on the sticky footer's top from
            // lg (index.css), where the footer starts to stick.
            bottom: "var(--brk-dock-bottom)",
            zIndex: 10,
            height: "var(--brk-pager-h)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mt: "auto",
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.default",
          }}
        >
          <Typography
            sx={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              typography: "body2",
              color: "text.secondary",
            }}
          >
            Showing {from}–{to} of {total}
            {def.totalNoun ? ` ${def.totalNoun}` : ""}
          </Typography>
          <Box sx={{ display: "flex", flexShrink: 0, gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={url.page <= 1}
              onClick={() => url.setPage(url.page - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={url.page >= lastPage}
              onClick={() => url.setPage(url.page + 1)}
            >
              Next
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

/** The list's title, its context line and its call to action. */
function ListHeader({
  title,
  context,
  action,
}: {
  title: string;
  context?: ReactNode;
  action?: ReactNode;
}) {
  // Every page's header (BRK-S262). The navbar's breadcrumb names the list
  // (BRK-S269), so its title is the page's h1 for screen readers only, and the
  // header shows the context line and the action.
  return (
    <PageHeader title={title} titleHidden meta={context} actions={action} />
  );
}

/** A list PMS doesn't serve yet (BRK-S251): the header, then the columns it
 *  will have over the not-yet-available panel. Below md a list is cards, which
 *  have no header row, so the panel stands alone there. Nothing loads. */
function PendingListView({ def }: { def: PendingListViewDef }) {
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });

  return (
    <Box component="section">
      <ListHeader title={def.title} context={def.context} />
      {wide ? (
        <Frame>
          <Table size="small" aria-label={def.title}>
            <TableHead>
              <TableRow>
                {def.columns.map((column) => (
                  <TableCell key={column} sx={headerCellSx}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          </Table>
          <NotYetAvailable {...def.notYetAvailable} framed={false} />
        </Frame>
      ) : (
        <NotYetAvailable {...def.notYetAvailable} />
      )}
    </Box>
  );
}

function Toolbar<Row>({
  def,
  url,
  filterOptions,
  clearable,
}: {
  def: BaseListViewDef<Row>;
  url: ReturnType<typeof useListUrlState>;
  filterOptions: Record<string, FilterOptions>;
  clearable: boolean;
}) {
  // The typed search applies once the broker pauses (BRK-S315), or at once on
  // Enter; it follows the URL when that changes under it (Back, "Clear
  // search"). A late answer is the load's to drop, as for any change.
  const [draft, setDraft] = useState(url.search);
  useEffect(() => setDraft(url.search), [url.search]);
  const paused = useDebouncedValue(draft, SEARCH_PAUSE_MS);
  const latest = useRef(url);
  latest.current = url;
  useEffect(() => {
    if (paused.trim() !== latest.current.search)
      latest.current.setSearch(paused);
  }, [paused]);

  function onSearch(event: FormEvent) {
    event.preventDefault();
    url.setSearch(draft);
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1.5,
        mb: 2,
      }}
    >
      {def.search && (
        <Box
          component="form"
          role="search"
          onSubmit={onSearch}
          sx={{ display: "flex", gap: 1, flex: "1 1 18rem", maxWidth: "32rem" }}
        >
          <OutlinedInput
            size="small"
            type="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={def.search.placeholder}
            inputProps={{ "aria-label": def.search.label }}
            startAdornment={
              <InputAdornment position="start">
                <Search size={16} aria-hidden />
              </InputAdornment>
            }
            sx={{ flex: 1, minWidth: 0, bgcolor: "background.paper" }}
          />
          <Button type="submit" variant="outlined">
            Search
          </Button>
        </Box>
      )}
      {def.toggles?.map((toggle) => (
        <FormControlLabel
          key={toggle.apiParam}
          label={toggle.label}
          sx={{ mr: 0 }}
          control={
            <Checkbox
              size="small"
              checked={url.toggles[toggle.apiParam] ?? false}
              onChange={(event) =>
                url.setToggle(toggle.apiParam, event.target.checked)
              }
            />
          }
        />
      ))}
      {def.filters?.map((filter) => (
        <FilterControl
          key={filter.apiParam}
          filter={filter}
          options={filterOptions[filter.apiParam] ?? "loading"}
          value={url.filters[filter.apiParam]}
          onChange={(value) => url.setFilter(filter.apiParam, value)}
        />
      ))}
      {clearable && (
        <Button variant="text" size="small" onClick={url.clearFilters}>
          Clear filters
        </Button>
      )}
      {def.sort && url.sort && (
        <SortControl sort={def.sort} value={url.sort} onChange={url.setSort} />
      )}
      {def.linkParams?.map((link) =>
        url.links[link.apiParam] ? (
          <Typography
            key={link.apiParam}
            sx={{
              flexBasis: "100%",
              typography: "body2",
              color: "text.secondary",
            }}
          >
            <span>{link.note}</span> ·{" "}
            {/* A real link: the narrowed and the whole list are two URLs,
                and Back returns to the narrowed one. */}
            <Link
              component={RouterLink}
              to={{ search: url.searchWithout(link.apiParam) }}
              underline="hover"
              sx={{ fontSize: "inherit" }}
            >
              Show all {def.noun}
            </Link>
          </Typography>
        ) : null,
      )}
    </Box>
  );
}

/** A select over the values PMS accepts; its first option sends nothing. One
 *  whose options come from PMS is disabled until they arrive, and says so if
 *  they can't be read. */
function FilterControl({
  filter,
  options,
  value,
  onChange,
}: {
  filter: ListFilter;
  options: FilterOptions;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const id = useId();
  const ready = typeof options !== "string";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <FormLabel
        htmlFor={id}
        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
      >
        {filter.label}
      </FormLabel>
      <NativeSelect
        id={id}
        value={ready ? (value ?? "") : ""}
        disabled={!ready}
        input={<OutlinedInput size="small" />}
        onChange={(event) => onChange(event.target.value || undefined)}
        sx={{ minWidth: "11rem", bgcolor: "background.paper" }}
      >
        {ready ? (
          <>
            <option value="">{filter.allLabel}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </>
        ) : (
          <option value="">
            {options === "loading" ? "Loading…" : "Couldn’t load"}
          </option>
        )}
      </NativeSelect>
    </Box>
  );
}

/** "Sort by" on the right of the toolbar: each option, newest or oldest first. */
function SortControl({
  sort,
  value,
  onChange,
}: {
  sort: ListSort;
  value: ListSortState;
  onChange: (next: ListSortState) => void;
}) {
  const id = useId();
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
      <FormLabel
        htmlFor={id}
        sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
      >
        Sort by
      </FormLabel>
      <NativeSelect
        id={id}
        value={`${value.field}:${value.dir}`}
        input={<OutlinedInput size="small" />}
        onChange={(event) => {
          const [field, dir] = event.target.value.split(":");
          onChange({ field, dir: dir === "asc" ? "asc" : "desc" });
        }}
        sx={{ minWidth: "15rem", bgcolor: "background.paper" }}
      >
        {sort.options.flatMap((option) =>
          (["desc", "asc"] as const).map((dir) => (
            <option
              key={`${option.value}:${dir}`}
              value={`${option.value}:${dir}`}
            >
              {`${option.label} (${(option.directions ?? DATE_DIRECTIONS)[dir]})`}
            </option>
          )),
        )}
      </NativeSelect>
    </Box>
  );
}

/** The row's link: it stretches over the whole row or card, so anywhere on it
 *  opens the record, while its name says which record. */
function TitleCell<Row>({
  def,
  column,
  row,
  view,
}: {
  def: BaseListViewDef<Row>;
  column: ListColumn<Row>;
  row: Row;
  view: ListViewState;
}) {
  const subtitle = column.subtitle?.(row);
  const href = def.rowHref(row);
  return (
    <>
      {href === null ? (
        // Nowhere to go: the title is text, and nothing is stretched over the
        // row to pretend otherwise.
        <Typography
          component="span"
          sx={{ fontSize: "inherit", fontWeight: 600, color: "text.primary" }}
        >
          {column.cell(row, view)}
        </Typography>
      ) : (
        <Link
          component={RouterLink}
          to={href}
          aria-label={def.rowLabel(row)}
          underline="hover"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            "&::after": { content: '""', position: "absolute", inset: 0 },
          }}
        >
          {column.cell(row, view)}
        </Link>
      )}
      {subtitle && (
        <Typography
          variant="caption"
          component="div"
          sx={{ color: "text.secondary" }}
        >
          {subtitle}
        </Typography>
      )}
    </>
  );
}

// The head row is 36px in the theme's head style (caption at 500, dim,
// sentence case), and a row 40px, or 52px with a subtitle (BRK-S263). A
// cell's height is its row's minimum, border included.
const headerCellSx = {
  bgcolor: "background.default",
  borderColor: "divider",
  height: 36,
  py: 0,
  whiteSpace: "nowrap",
} as const;

function cellSx<Row>(column: ListColumn<Row>) {
  return {
    borderColor: "divider",
    height: 40,
    py: 1,
    // A record number never breaks mid-number ("SB-" / "40699").
    ...(column.kind === "mono"
      ? {
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "0.01em",
          whiteSpace: "nowrap",
        }
      : {}),
  };
}

function Frame({
  children,
  scroll = false,
}: {
  children: ReactNode;
  /** A wide table scrolls inside the frame rather than pushing the page. */
  scroll?: boolean;
}) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        // A wide table scrolls sideways inside the frame (its rows still clip
        // vertically to the rounded corners).
        ...(scroll
          ? { overflowX: "auto", overflowY: "hidden" }
          : { overflow: "hidden" }),
        bgcolor: "background.paper",
      }}
    >
      {children}
    </Box>
  );
}

/** Row actions sit above the row's stretched link, so they stay clickable. */
const actionsSx = { position: "relative", zIndex: 1 } as const;

/** What a row that failed to render shows in its place (BRK-S259). The rest
 *  of the list stays usable, and the error goes to the console. */
const ROW_FALLBACK = "This row couldn’t be shown.";
const rowFallbackSx = {
  typography: "body2",
  color: "text.secondary",
  borderColor: "divider",
} as const;

function RowsTable<Row>({
  def,
  rows,
  view,
  context,
  totals,
}: {
  def: BaseListViewDef<Row>;
  rows: readonly Row[];
  view: ListViewState;
  context: ListRowContext;
  totals?: ListTotals;
}) {
  return (
    <Frame scroll={def.wide === true}>
      <Table size="small" aria-label={def.title}>
        <TableHead>
          <TableRow>
            {def.columns.map((column) => (
              <TableCell key={column.id} align={column.align} sx={headerCellSx}>
                {headerOf(column, view)}
              </TableCell>
            ))}
            {def.rowActions && (
              <TableCell align="right" sx={headerCellSx}>
                <Box component="span" sx={srOnly}>
                  Actions
                </Box>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <ErrorBoundary
              key={def.rowKey(row)}
              resetKeys={[row]}
              fallback={() => (
                <TableRow>
                  <TableCell
                    colSpan={def.columns.length + (def.rowActions ? 1 : 0)}
                    sx={rowFallbackSx}
                  >
                    {ROW_FALLBACK}
                  </TableCell>
                </TableRow>
              )}
            >
              <TableRowView def={def} row={row} view={view} context={context} />
            </ErrorBoundary>
          ))}
        </TableBody>
        {totals && (
          <TableFooter aria-label="Totals">
            <TableRow>
              {def.columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={totalsCellSx}
                >
                  {totals[column.id]}
                </TableCell>
              ))}
              {def.rowActions && <TableCell sx={totalsCellSx} />}
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </Frame>
  );
}

/** The totals row reads as the table's last line, in the body's own type. */
const totalsCellSx = {
  borderColor: "divider",
  borderTop: 1,
  borderBottom: 0,
  height: 40,
  py: 1,
  typography: "body2",
  fontWeight: 600,
  color: "text.primary",
  fontVariantNumeric: "tabular-nums",
} as const;

/** One table row. Its own component, so a cell that throws while rendering
 *  is caught by the row's boundary, not the list's (BRK-S259). */
function TableRowView<Row>({
  def,
  row,
  view,
  context,
}: {
  def: BaseListViewDef<Row>;
  row: Row;
  view: ListViewState;
  context: ListRowContext;
}) {
  return (
    <TableRow
      hover
      // A containing block for the stretched link (transform covers browsers
      // that ignore `position` on table rows).
      sx={{
        position: "relative",
        transform: "scale(1)",
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      {def.columns.map((column) => (
        <TableCell key={column.id} align={column.align} sx={cellSx(column)}>
          {column.kind === "title" ? (
            <TitleCell def={def} column={column} row={row} view={view} />
          ) : (
            column.cell(row, view)
          )}
        </TableCell>
      ))}
      {def.rowActions && (
        <TableCell
          align="right"
          sx={{
            ...actionsSx,
            borderColor: "divider",
            whiteSpace: "nowrap",
          }}
        >
          {def.rowActions(row, context)}
        </TableCell>
      )}
    </TableRow>
  );
}

function RowsCards<Row>({
  def,
  rows,
  view,
  context,
  totals,
}: {
  def: BaseListViewDef<Row>;
  rows: readonly Row[];
  view: ListViewState;
  context: ListRowContext;
  totals?: ListTotals;
}) {
  const list = (
    <Box
      component="ul"
      role="list"
      aria-label={def.title}
      sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 1.25 }}
    >
      {rows.map((row) => (
        <ErrorBoundary
          key={def.rowKey(row)}
          resetKeys={[row]}
          fallback={() => (
            <Box component="li" sx={{ ...cardSx, ...rowFallbackSx }}>
              {ROW_FALLBACK}
            </Box>
          )}
        >
          <CardView def={def} row={row} view={view} context={context} />
        </ErrorBoundary>
      ))}
    </Box>
  );
  if (!totals) return list;
  const title = def.columns.find((c) => c.kind === "title");
  const others = def.columns.filter(
    (c) => c.kind !== "title" && totals[c.id] != null,
  );
  // The table's footer as one more card: its label, then each summed column
  // under the header it sums.
  return (
    <>
      {list}
      <Box
        role="group"
        aria-label="Totals"
        sx={{ ...cardSx, mt: 1.25, bgcolor: "background.default" }}
      >
        {title && (
          <Typography sx={{ typography: "body2", fontWeight: 600 }}>
            {totals[title.id]}
          </Typography>
        )}
        <Box
          component="dl"
          sx={{
            m: 0,
            mt: 0.75,
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            columnGap: 1.5,
            rowGap: 0.5,
            typography: "body2",
          }}
        >
          {others.map((column) => (
            <Fragment key={column.id}>
              <Box component="dt" sx={{ color: "text.secondary" }}>
                {headerOf(column, view)}
              </Box>
              <Box
                component="dd"
                sx={{
                  m: 0,
                  fontWeight: 600,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {totals[column.id]}
              </Box>
            </Fragment>
          ))}
        </Box>
      </Box>
    </>
  );
}

const cardSx = {
  position: "relative",
  border: 1,
  borderColor: "divider",
  borderRadius: 2,
  bgcolor: "background.paper",
  p: 1.5,
} as const;

/** One card below md, its own component for the same reason as the table's
 *  rows (BRK-S259). */
function CardView<Row>({
  def,
  row,
  view,
  context,
}: {
  def: BaseListViewDef<Row>;
  row: Row;
  view: ListViewState;
  context: ListRowContext;
}) {
  const title = def.columns.find((c) => c.kind === "title");
  const others = def.columns.filter((c) => c.kind !== "title");
  return (
    <Box
      component="li"
      sx={{
        ...cardSx,
        transition: "border-color 150ms",
        "&:hover": { borderColor: "primary.main" },
      }}
    >
      {title && <TitleCell def={def} column={title} row={row} view={view} />}
      <Box
        component="dl"
        sx={{
          m: 0,
          mt: 1.25,
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          columnGap: 1.5,
          rowGap: 0.5,
          typography: "body2",
        }}
      >
        {others.map((column) => (
          <Fragment key={column.id}>
            <Box component="dt" sx={{ color: "text.secondary" }}>
              {headerOf(column, view)}
            </Box>
            <Box component="dd" sx={{ m: 0, overflowWrap: "anywhere" }}>
              {column.cell(row, view)}
            </Box>
          </Fragment>
        ))}
      </Box>
      {def.rowActions && (
        <Box
          sx={{
            ...actionsSx,
            mt: 1.25,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {def.rowActions(row, context)}
        </Box>
      )}
    </Box>
  );
}

/** The first load's placeholder, in the layout the rows will take. */
function SkeletonRows<Row>({
  def,
  view,
  wide,
}: {
  def: BaseListViewDef<Row>;
  view: ListViewState;
  wide: boolean;
}) {
  const rows = Array.from({ length: SKELETON_ROWS }, (_, i) => i);
  if (!wide) {
    return (
      <Box aria-hidden sx={{ display: "grid", gap: 1.25 }}>
        {rows.slice(0, 3).map((i) => (
          <Box
            key={i}
            data-testid="list-skeleton-row"
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
              p: 1.5,
            }}
          >
            <Skeleton width="55%" />
            <Skeleton width="80%" />
            <Skeleton width="40%" />
          </Box>
        ))}
      </Box>
    );
  }
  return (
    <Box aria-hidden>
      <Frame scroll={def.wide === true}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {def.columns.map((column) => (
                <TableCell key={column.id} sx={headerCellSx}>
                  {headerOf(column, view)}
                </TableCell>
              ))}
              {/* The actions cell too, so the table doesn't shift on load. */}
              {def.rowActions && <TableCell sx={headerCellSx} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((i) => (
              <TableRow key={i} data-testid="list-skeleton-row">
                {def.columns.map((column) => (
                  <TableCell key={column.id} sx={cellSx(column)}>
                    <Skeleton width={column.kind === "title" ? "80%" : "60%"} />
                  </TableCell>
                ))}
                {def.rowActions && (
                  <TableCell sx={{ borderColor: "divider" }}>
                    <Skeleton width="70%" />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Frame>
    </Box>
  );
}

function EmptyState<Row>({
  def,
  search,
  filtered,
  pastTheEnd,
  onClearSearch,
  onClearFilters,
  onFirstPage,
}: {
  def: BaseListViewDef<Row>;
  search: string;
  filtered: boolean;
  pastTheEnd: boolean;
  onClearSearch: () => void;
  onClearFilters: () => void;
  onFirstPage: () => void;
}) {
  const [title, body, action]: [string, ReactNode, ReactNode] = pastTheEnd
    ? [
        "There’s nothing on this page",
        "The list is shorter than it was.",
        <Button
          key="first"
          variant="outlined"
          size="small"
          onClick={onFirstPage}
        >
          First page
        </Button>,
      ]
    : filtered
      ? [
          search
            ? `No ${def.noun} match “${search}”`
            : `No ${def.noun} match these filters`,
          "Try another search, or clear it to see everything.",
          search ? (
            <Button
              key="clear"
              variant="outlined"
              size="small"
              onClick={onClearSearch}
            >
              Clear search
            </Button>
          ) : (
            <Button
              key="clear"
              variant="outlined"
              size="small"
              onClick={onClearFilters}
            >
              Clear filters
            </Button>
          ),
        ]
      : [def.empty.title, def.empty.body, def.empty.action];

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderStyle: "dashed",
        borderRadius: 2,
        bgcolor: "background.paper",
        px: 3,
        py: 5,
        textAlign: "center",
      }}
    >
      <Typography variant="h2" component="p">
        {title}
      </Typography>
      {body && (
        <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }}>
          {body}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
}
